// AON-06 (rev1-creds C4) — where the client records the silent always-on mount.
//
// AON-03 deliberately writes nothing into workspaceStore.mountedById for the
// always-on space (`hideAlwaysOnWorkspace`): every workspace list stays blind to
// it until AON-08 ships the visible space UI. The space view and the settings
// "Workflow connections" block still need to know whether the space is mounted
// in this session and whether it is usable (state "on", not suspended), so the
// mount has its own tiny store.
//
// A leaf module on purpose: workspaceStore (logout) and the settings search
// index import it, and neither may pull in alwaysOnAutoMount.ts, which imports
// workspaceStore itself (an import cycle).
import { create } from 'zustand';

export interface AlwaysOnMountState {
  /** The mounted always-on space, or null when none is mounted. */
  workspaceId: string | null;
  /** The mount response resolved in this session. */
  mounted: boolean;
  /** Mounted, state "on" and not suspended — the space can take moves. */
  usable: boolean;
  /** The space's display name from the status read ("" when unnamed). */
  name: string;
}

/** The always-on space view (app/(dashboard)/workspaces/always-on). The move
 *  dialog's success state and the settings block link to it; AON-08 owns a
 *  navigation entry. */
export const ALWAYS_ON_SPACE_HREF = '/workspaces/always-on';

const INITIAL: AlwaysOnMountState = { workspaceId: null, mounted: false, usable: false, name: '' };

export const useAlwaysOnMountStore = create<AlwaysOnMountState>()(() => ({ ...INITIAL }));

/** Forget the always-on mount. Called at the start of every auto-mount run, on
 *  its failure, and on logout / session-key eviction. Idempotent. */
export function resetAlwaysOnMount(): void {
  useAlwaysOnMountStore.setState({ ...INITIAL });
}

/** Records a resolved mount, or re-records `usable` for the mounted space.
 *  Callers: alwaysOnAutoMount (the mount itself), and since AON-08 the space
 *  status store (alwayson/alwaysOnSpaceStore.ts `syncMount` — every confirmed
 *  status for the mounted workspace). The consent dialog and the off switch do
 *  NOT call it: they write the status through the space store, which does. */
export function recordAlwaysOnMount(workspaceId: string, usable: boolean, name = ''): void {
  useAlwaysOnMountStore.setState({ workspaceId, mounted: true, usable, name });
}

/** Synchronous read for non-React callers. */
export function isAlwaysOnSpaceUsable(): boolean {
  return useAlwaysOnMountStore.getState().usable;
}

/** Mounted in this session, usable or not. The settings "Workflow connections"
 *  block renders on this (an off or suspended space still lets the owner move a
 *  credential back to Private), so the search index entry gates on it too. */
export function isAlwaysOnSpaceMounted(): boolean {
  return useAlwaysOnMountStore.getState().mounted;
}
